<script setup lang="ts">
import { callWithNuxt } from 'nuxt/app';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import HotelLandingViewSection from '../_components/HotelLandingViewSection.vue';
import HotelCityCategories from './_components/HotelCityCategories.vue';
import { useViewSectionsHandler } from '@composable/_local/hotel&view/useViewSectionsHandler';
import { useFilters } from '@composable/_local/hotel&view/useFilters';
import { useInit } from '@composable/_local/hotel&view/useInit';
import SortBox from '@components/ui/hotel&view/sort&filter/SortBox.vue';
import FiltersBox from '@components/ui/hotel&view/sort&filter/FiltersBox.vue';
import ViewSeoContent from '@components/ui/hotel&view/ViewSeoContent.vue';
import ViewFaqs from '@components/ui/hotel&view/ViewFaqs.vue';
import ChangeSearch from '@components/ui/hotel&view/ChangeSearch.vue';
import EmptyList from '@components/ui/hotel&view/EmptyList.vue';
import LoadingError from '@components/ui/hotel&view/LoadingError.vue';
import RealTimeLoading from '@components/ui/hotel&view/RealTimeLoading.vue';
import MobileActionBar from '@components/ui/hotel&view/MobileActionBar.vue';
import LoadingModal from '@components/ui/hotel&view/LoadingModal.vue';
import ResidenceViewSection from '@components/ui/hotel&view/ResidenceViewSection.vue';
import { type FaqFaqItemInner } from '@clients/ContentApi';
import { type BreadcrumbItem } from '#imports';
import ContentAlert from '@components/ui/index&view&widget/ContentAlert.vue';
import ReserveError from '@components/util/ReserveError.vue';
import TopBanners from '@components/ui/index&view/TopBanners.vue';
import 'assets/scss/shepherd.scss';
import { type HotelPathData } from '@utils/hotel';
import { type VerifyPathError } from '@utils/itinerary';
import { LoadingStatus } from '@itinerary/ItineraryModuleTypes';
import { usePrefetch } from '@composable/_local/hotel&view/usePrefetch';
import LoginNotification from '@components/ui/hotel&view/LoginNotification.vue';
import ViewHeader from '@components/ui/hotel&view/ViewHeader.vue';
import SearchHotelForm from '@components/ui/index&view&widget/forms/SearchHotelForm.vue';

//#region definitions
const itineraryStore = useItineraryStore();
const { noResults, loading } = storeToRefs(itineraryStore);
const searchFormStore = useSearchFormStore();
const hotelModule = useHotelModule();
const { isOpened } = useClosable('filters');
const {
    description,
    canonicalRegex,
    currentLoadingStatus,
    metaTagTitle,
    currentStar,
} = moduleToRefs(hotelModule);
const hotelForm = useHotelForm();
const contentStore = useContentStore();
const { searchResultDescription, loadingBanners } = storeToRefs(contentStore);
const layoutStore = useLayoutStore();
const { isExtraFeaturesEnabled } = storeToRefs(layoutStore);
const route = useRoute();
const router = useRouter();

const isStarPage = computed(() => !!route.params.stars);
const pathData = computed(
    () =>
        ({
            ...route.params,
            ...route.query,
            static: showLanding.value ? 'true' : undefined,
        }) as HotelPathData
);
const siteConfigs = useSiteConfigs();
const isLocked = useScrollLock(
    typeof document === 'undefined' ? null : document.body
);
const changeSearchRef = ref<InstanceType<typeof ChangeSearch>>();
const { isMobile } = useSize();
const { start, stop, forceReset } = useItineraryPackageReload(() =>
    itineraryStore.loadAvailable()
);
const forceReload = () => {
    forceReset();
    itineraryStore.forceLoadAvailable();
};
const i18n = useI18n();
const { isOnline } = useNetwork();
const showChangeSearch = ref(false);
const fixBoxEnabled = ref(true);
const showingSuggestions = ref(false);
const changeSearchFixed = ref(false);
const showHotelsOnMap = ref(false);
const isShowLoadingModal = ref(layoutStore.ssrIsShowLoadingModal);
//#endregion

//#region computed
const showLanding = computed(
    () => !route.query.fromDate || !route.query.toDate
);

const loadingBanner = computed(() =>
    loadingBanners.value
        .filter(b => new RegExp(b.urlPattern || '').test(route.path))
        .filter(
            m =>
                !m.displaySize ||
                m.displaySize === 'both' ||
                (m.displaySize === 'mobile' ? isMobile.value : !isMobile.value)
        )
);

const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
    const list = [
        {
            title: 'مِستربلیط',
            link: '/',
        },
        {
            title: 'رزرو هتل',
            link: '/hotel',
        },
        {
            title: `رزرو هتل ${itineraryStore.targetPersianName}`,
            link: route.path.replace(/\/\w*-star/, ''),
        },
    ];
    if (isStarPage.value) {
        list.push({
            title: `رزرو هتل‌های ${currentStar.value?.title} ستاره ${itineraryStore.targetPersianName}`,
            link: route.path,
        });
    }
    return list;
});

const faqs = computed(() => {
    const faqItems = contentStore.searchResultFaqs;
    if (faqItems.length) {
        return [
            {
                title: '',
                items: faqItems.map((faq: FaqFaqItemInner) => ({
                    title: faq.question || '',
                    value: faq.answer || '',
                })),
            },
        ];
    } else {
        return [];
    }
});

const faqTitle = computed(
    () => `سؤالات رزرو هتل ${itineraryStore.targetPersianName}`
);

const messages = computed(() => {
    if (!siteConfigs.showBanners) {
        return [];
    }
    return contentStore.filteredMessages.filter(m =>
        new RegExp(m.urlPattern || '').test(route.path)
    );
});

const canonicalUrl = computed(
    () =>
        `https://mrbilit.com${route.path
            .replace(canonicalRegex.value, '$1')
            .replace(/\/$/, '')}`
);

const metaTagDescription = computed(
    () => searchResultDescription.value || description.value!
);

const showEmptyList = computed(
    () =>
        itineraryStore.isLoadedSuccess &&
        (itineraryStore.noFilteredResults || itineraryStore.noResults)
);

const showLoading = computed(
    () => isShowLoadingModal.value && !!Object.keys(route.query).length
);
//#endregion

//#region methods
const closeLoadingModal = () => {
    isShowLoadingModal.value = false;
    layoutStore.ssrIsShowLoadingModal = false;
};
const openLoadingModal = () => {
    if (loadingBanner.value[0]) {
        isShowLoadingModal.value = true;
        if (process.server) {
            layoutStore.ssrIsShowLoadingModal = true;
        }
    }
};
const init = useInit(pathData, route.path);
const prefetch = usePrefetch(pathData, route.path);
const initPage = async () => {
    await init();
    start();
};
const showSearch = () => {
    if (isMobile.value) {
        changeSearchRef.value?.show();
    }
};
const onscroll = () => {
    const footer = (document as any).getElementById('__footer');
    if (footer) {
        fixBoxEnabled.value =
            footer.getBoundingClientRect().y >= window.innerHeight;
    }
};
const {
    openFilters: open,
    closeFilters: close,
    applyFilters: apply,
    sortRef,
    filtersRef,
    showFiltersInMobile,
} = useFilters();
const { onViewReservableList } = useViewSectionsHandler();
const toggleShowMaps = () => {
    showHotelsOnMap.value = !showHotelsOnMap.value;
};
//#endregion

//#region watches
watch(showHotelsOnMap, v => {
    isLocked.value = v;
});
watch(isOpened, value => {
    isLocked.value = isMobile.value && value;
});
watch(loading, val => {
    if (val) {
        openLoadingModal();
    } else {
        closeLoadingModal();
    }
});

//#endregion

//#region page setup
onServerPrefetch(async () => {
    contentStore.searchResultContentsLoading = true;
    if (showLanding.value) {
        await prefetch();
    }
    if (!hotelForm.searchReady) {
        try {
            await hotelForm.verifyViewPath(route.path, pathData.value);
            await searchFormStore.loadViewPath(route.path, pathData.value);
            await Promise.all([
                contentStore.getSearchResultContents(),
                contentStore.loadBanners(),
            ]);
        } catch (e) {
            const err = e as VerifyPathError;
            const app = useNuxtApp();
            callWithNuxt(app!, async () => {
                if (err.needRedirect && err.status && err.newUrl) {
                    await navigateTo(
                        {
                            path: err.newUrl,
                            query: { ...route.query, ...err.queries },
                        },
                        { redirectCode: err.status, replace: err.replace }
                    );
                } else if (
                    err.status !== undefined &&
                    err.message !== undefined
                ) {
                    showError({
                        statusCode: err.status,
                        message: err.message,
                    });
                } else {
                    grayLog()?.log(`${e}`, JSON.stringify(e), {
                        path: route.fullPath,
                    });
                    reportException(e);
                    showError({ statusCode: 500 });
                }
            });
        }
    }
    openLoadingModal();
});
useAsyncData(
    async () => {
        try {
            if (showLanding.value) {
                hotelModule.reviveAllPackages();
            }
            if (route.hash) {
                router.replace(route.fullPath.replace(/#.*/, ''));
            }
            await initPage();
            if (showLanding.value) {
                hotelModule.loadFilters();
                hotelModule.loadHotelPrices();
            }
            contentStore.loadViewPageContents();
            contentStore.getSearchResultContents();
        } catch (e) {
            reportException(e);
        }
    },
    { lazy: true, server: false }
);
useHead({
    htmlAttrs: {
        class: 'hide-header-in-mobile',
    },
    link: [
        {
            rel: 'canonical',
            href: canonicalUrl,
        },
    ],
});
useSeoMeta({
    title: metaTagTitle,
    robots: () =>
        Object.keys(route.query).length ? 'noindex, follow' : 'index, follow',
    ogTitle: metaTagTitle,
    description: metaTagDescription,
    ogDescription: metaTagDescription,
    ogUrl: canonicalUrl,
});
definePageMeta({
    layout: 'buy-flow',
});
//#endregion

//#region lifecycle
onBeforeRouteLeave((to, from, next) => {
    if (to.path === from.path) {
        next();
        return;
    }
    const [, first, second] = to.fullPath.split('/');
    if (
        first !== 'reserve' &&
        (!['flights', 'trains', 'buses', 'taxi'].includes(first) || !second)
    ) {
        itineraryStore.currentSetIndex = 0;
    }
    next();
});
onMounted(() => {
    window.addEventListener('scroll', onscroll);
    if (showLanding.value) {
        const curr = useCurrentSearchForm();
        curr.value.setItinerarySearchDates({
            start: dayConfig.gregory().startOf('day').toDate(),
            end: dayConfig.gregory().add(1, 'day').startOf('day').toDate(),
        });
    }
});
onBeforeUnmount(() => {
    stop();
    window.removeEventListener('scroll', onscroll);
});
//#endregion
</script>

<template>
    <div class="view-page-wrapper">
        <ClientOnly>
            <TopBanners />
        </ClientOnly>
        <template v-if="loadingBanner[0]">
            <Transition>
                <LoadingModal
                    v-show="showLoading"
                    :banner="loadingBanner[0]"
                    reserve-type="هتل"
                    :destination="hotelForm.userDestination?.title || ''"
                />
            </Transition>
        </template>
        <MobileActionBar
            fixed
            have-map-button
            @toggle-map="toggleShowMaps"
            @open-filters="open"
        />
        <ChangeSearch
            ref="changeSearchRef"
            :fix-form-to-top="showHotelsOnMap"
            :remove-mobile-bottom-sheet="!showHotelsOnMap && showLanding"
            @change-suggestions="showingSuggestions = $event"
            @change-fixed="changeSearchFixed = $event"
            @force-reload="forceReload"
        />
        <div class="view-panel-container">
            <mr-fix
                :offset-top="showingSuggestions ? 128 : 76"
                class="view-sidebar"
                stop-element="#view-stop-element"
                :class="{
                    'view-sidebar-mobile-visible': showChangeSearch,
                    'show-filters-in-mobile': showFiltersInMobile,
                }"
                element="aside"
                :disabled="isMobile"
            >
                <div class="fix-content">
                    <ViewHeader
                        :hide-date-in-title="showLanding"
                        :disable-change-search="showLanding"
                        @show-search="showSearch"
                        @toggle-maps="toggleShowMaps"
                    />
                    <SearchHotelForm
                        v-if="showLanding"
                        class="landing-change-search"
                        is-change-search
                    />
                    <RealTimeLoading desktop-only margin="0 0 8px 0" />
                    <div class="filters-header mobile">
                        <mr-button
                            size="big"
                            variant="transparent"
                            :icon-button="{
                                icon: faArrowRight,
                                color: 'white',
                            }"
                            @click="close"
                        />
                        <div class="filters-title">فیلتر و مرتب‌سازی</div>
                        <mr-button :icon-button="faArrowRight" class="hidden" />
                    </div>
                    <div class="filters-container">
                        <SortBox
                            v-show="!noResults"
                            ref="sortRef"
                            :auto-apply="!isMobile"
                        />
                        <FiltersBox ref="filtersRef" :auto-apply="!isMobile" />
                    </div>
                    <div class="btn-accept">
                        <mr-button full-width @click="apply"> تایید </mr-button>
                    </div>
                </div>
            </mr-fix>
            <div class="view-body">
                <ContentAlert
                    v-for="message in messages"
                    :key="message.id"
                    :message
                />
                <mr-breadcrumb
                    :items="breadcrumbItems"
                    class="breadcrumb"
                    theme="dark"
                />
                <ReserveError
                    v-if="!isOnline"
                    wrapper-id="connection-lost-offline"
                    :error="i18n.t('TrainErrors.noInternet')"
                    @research="router.replace('/')"
                />
                <HotelLandingViewSection
                    v-if="showLanding"
                    :show-hotels-on-map
                    @close-map="showHotelsOnMap = false"
                    @open-filters="open"
                    @view="onViewReservableList"
                />
                <ResidenceViewSection
                    v-else
                    :show-hotels-on-map
                    @close-map="showHotelsOnMap = false"
                    @open-filters="open"
                    @view="onViewReservableList"
                />
                <Transition>
                    <EmptyList v-show="showEmptyList" />
                </Transition>
                <Transition>
                    <LoadingError
                        v-if="currentLoadingStatus === LoadingStatus.Error"
                    />
                </Transition>
            </div>
        </div>
        <nuxt-page :current-star />
        <div id="view-stop-element" style="margin-top: -4px" />
        <template v-if="isExtraFeaturesEnabled">
            <ViewFaqs :title="faqTitle" :items="faqs" />
            <ViewSeoContent />
            <HotelCityCategories v-if="!isStarPage" />
        </template>
        <LoginNotification />
    </div>
</template>

<style scoped lang="scss">
@include v-transition-opacity(0.3s);
.mobile {
    display: none;
    @include mobile-only {
        display: flex;
    }
}
.desktop {
    display: flex;
    @include mobile-only {
        display: none !important;
    }
}

.view-page-wrapper {
    position: relative;
}

.breadcrumb {
    margin-bottom: 4px;
    margin-top: 8px;
    @include mobile-only {
        margin-block: -4px;
    }
}

.show-map-container {
    @include shadow(1);
    position: relative;
    width: 100%;
    height: 140px;
    background-image: url('/img/Map.png');
    border-radius: 12px;
    margin-bottom: 8px;
    overflow: hidden;

    .show-map-button {
        position: absolute;
        bottom: 0;
        background: white !important;
        border-radius: 0 !important;
    }
    @include mobile-only() {
        display: none;
    }
}
.view-panel-container {
    display: flex;
    width: 100%;
    max-width: $app-container-max-width;
    margin: 0 auto;

    .view-sidebar {
        position: relative;
        flex-basis: 25%;
        flex-grow: 25;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        width: 25%;
        min-width: 220px;
        margin: 0 0 0 12px;
        min-height: 200px;
        .fix-content {
            display: flex;
            flex-direction: column;
            @include desktop-only {
                gap: 8px;
                padding-top: 8px;
            }
        }
        .filters-container {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
        .landing-change-search {
            display: none;
            @include mobile-only() {
                display: flex;
                padding-top: 16px;
            }
        }
    }

    .view-body {
        position: relative;
        flex-basis: 75%;
        flex-grow: 75;
        width: 70%;
    }
}

.cards-container {
    margin-top: 4px;
}

.mobile-only {
    display: none;
}

.btn-accept,
.filters-header {
    display: none;
}

@media (max-width: $app-container-max-width) {
    .view-panel-container {
        padding: 0 16px;
    }
}

@media (max-width: $app-mobile-max-width) {
    .view-panel-container {
        position: relative;
        flex-direction: column;
        padding: 0;

        .view-sidebar {
            flex: 0;
            width: 100%;
            padding: 0;
            min-height: auto;
            min-width: auto;
            .filters-container {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: usePalette($p-cool-gray, $p-100);
                z-index: 1000100;
                overflow-y: auto;
                padding: 84px 12px 80px;
                transition: 400ms all;
                transform: translateY(100vh);
            }
            &.show-filters-in-mobile {
                .filters-header {
                    height: 72px;
                    width: 100%;
                    position: fixed;
                    top: 0;
                    right: 0;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px;
                    background-color: usePalette();
                    z-index: 10000010;
                    .filters-title {
                        @include tg-display4($w7, usePalette($p-gray, $p-900));
                        margin: 0;
                        color: white;
                    }
                    .hidden {
                        visibility: hidden;
                    }
                }
                .filters-container {
                    transform: translateY(0);
                }

                .btn-accept {
                    display: flex;
                    width: 100%;
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background-color: white;
                    padding: 8px;
                    z-index: 10000010;
                    transform: translateZ(0);
                    > * {
                        margin-left: 8px;
                        margin-right: 8px;
                        &:first-child {
                            margin-right: 0;
                        }
                        &:last-child {
                            margin-left: 0;
                        }
                    }
                }
            }
        }

        .view-body {
            flex-grow: 1;
            width: 100%;
            padding: 8px 8px 66px 8px;
        }
    }

    .view-sidebar-mobile-visible {
        display: block;
    }

    .mobile-only {
        display: block;
    }
}
.fake-preload-image {
    display: none !important;
}
</style>
